import React, { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { TextStyled, useBreakpoint, useMatchMedia } from '@nike/nike-design-system-components';
import { RecentlyViewedContainer, HeaderContainer, ClearButton, StyledList } from './styles';
import { ErrorBoundary } from '../AppErrorBoundary';
import RecentlyViewedProductCard from './ProductCard';
import { useNavOptionsContext } from '../../context/optionsContext';
import templateString from '../../utils/templateString';
import useBodega from '../../utils/hooks/bodegaHook';
import useLocale from '../../utils/hooks/localeHook';
import useLocalStorage from '../../utils/hooks/localStorageHook';
import logger from '../../utils/logger';
import { BODEGA_DEFAULTS } from '../../constants';
import { AnalyticsContext } from '../../context/analyticsContext';
import fetchProductFeeds from './fetchProductFeeds';

const RecentlyViewed = () => {
  const { CLEAR, RECENTLY_VIEWED } = BODEGA_DEFAULTS;
  const [options, setOptions] = useState({ productIds: [] });
  const [recentlyViewedProducts, setRecentlyViewedProducts] = useState([]);
  const [useSquarishOnly, setUseSquarishOnly] = useState(false);
  const [getLocalStorage, setLocalStorage, clearLocalStorage] = useLocalStorage(
    'viewed:products',
    [],
  );
  const { cloudUrlFragment, countryOrMarketplaceId: country, language } = useLocale();
  const { opts = {}, mounted } = useNavOptionsContext() || {};
  const isMobileQuery = useMatchMedia(useBreakpoint('lt', 'm'));
  // Prevent hydration mismatch: assume desktop during SSR and initial render
  const isMobile = mounted ? isMobileQuery : false;
  const clearButtonTitle = useBodega('nav_header_label_clear_button', CLEAR);
  const recentlyViewedTitle = useBodega('nav_header_label_recently_viewed', RECENTLY_VIEWED);
  const productCountLabel = templateString(
    useBodega('nav_header_label_search_product_count') ||
      BODEGA_DEFAULTS.SEARCH_PRODUCT_COUNT_RESULTS,
    {
      filteredProductCount: recentlyViewedProducts.length.toString(),
    },
  );
  const searchResultsRef = useRef(null);
  const { analyticsClient } = React.useContext(AnalyticsContext);
  const [recentlyViewedInViewRef, recentlyViewedInView] = useInView({ threshold: 0.5 });

  useEffect(() => {
    try {
      if (!mounted) return;
      if (opts?.appInfo?.viewChannel?.toUpperCase() !== 'PDP')
        throw new Error('invalid view channel');
      const styleColor = opts?.redirectGeomismatch()?.styleColor;
      if (!styleColor) throw new Error('styleColor not found');
      setLocalStorage(styleColor);
      // We explicitly not updating the options state here because we do not want
      // to show the product the user is currently viewing.
      // If they refresh they will see the product however.
    } catch (error) {
      logger.error('Error updating recently viewed products:', error);
    }
    // Should this be here or its own useEffect?
    const storedProductIds = getLocalStorage();
    setOptions(prevOptions => ({
      ...prevOptions,
      productIds: storedProductIds.slice(0, 4),
    }));
  }, [mounted]);

  useEffect(() => {
    const fetchData = async () => {
      const products = await fetchProductFeeds({
        country,
        language,
        logger,
        styleColors: options.productIds,
        cloudUrlFragment,
      });
      if (!products) return;
      // Check if any product is missing portraitURL
      const anyMissingPortrait = products.some(product => !product?.colorwayImages?.portraitURL);
      setUseSquarishOnly(anyMissingPortrait);
      setRecentlyViewedProducts(products);
    };
    if (options?.productIds?.length > 0) {
      fetchData();
    }
  }, [options]);

  useEffect(() => {
    if (recentlyViewedInView) {
      analyticsClient?.recentlyViewedExperienceViewed();
    }
  }, [recentlyViewedInView]);

  const clearRecentlyViewed = () => {
    clearLocalStorage();
    setOptions({
      productIds: [],
    });
    setRecentlyViewedProducts([]);
    analyticsClient?.recentlyViewedProductsCleared();
  };

  return (
    <ErrorBoundary>
      {recentlyViewedProducts?.length > 0 && isMobile && (
        <RecentlyViewedContainer data-testid="recently-viewed" ref={recentlyViewedInViewRef}>
          <HeaderContainer>
            <TextStyled
              Component="p"
              appearance="body2"
              color="secondary"
              data-testid="recently-viewed-title"
            >
              {recentlyViewedTitle}
            </TextStyled>
            <ClearButton
              appearance="text"
              ariaLabel="Clear Recently Viewed"
              background="light"
              data-testid="clear-recently-viewed-button"
              disableRipple={false}
              disabled={false}
              onClick={clearRecentlyViewed}
              size="small"
              type="button"
            >
              {clearButtonTitle}
            </ClearButton>
          </HeaderContainer>
          <StyledList
            aria-label={productCountLabel}
            aria-live="assertive"
            data-testid="recently-viewed-products"
          >
            {recentlyViewedProducts.map((product, index) => (
              <RecentlyViewedProductCard
                index={index}
                key={index}
                product={product}
                resultsLength={recentlyViewedProducts.length}
                searchResultsRef={index === 0 ? searchResultsRef : null}
                searchTerm={''}
                useSquarishOnly={useSquarishOnly}
              />
            ))}
          </StyledList>
        </RecentlyViewedContainer>
      )}
    </ErrorBoundary>
  );
};

export default RecentlyViewed;
